/* ==========================================================================
   GYMJET — product.js (product pages only)
   ========================================================================== */
(() => {
  'use strict';

  const GJ = (window.GJ = window.GJ || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const cfg = () => GJ.config || { strings: {}, routes: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isRTL = () => document.documentElement.dir === 'rtl';
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    const gallery = $('[data-gallery]', root);
    if (!gallery) return null;
    const track = $('[data-gallery-track]', gallery);
    const items = $$('.gallery__item', track);
    const thumbs = $$('[data-thumb]', gallery);
    const counter = $('[data-gallery-counter]', gallery);
    const prev = $('[data-gallery-prev]', gallery);
    const next = $('[data-gallery-next]', gallery);
    let index = 0;

    const go = (i) => {
      const target = Math.max(0, Math.min(items.length - 1, i));
      track.scrollTo({ left: (isRTL() ? -1 : 1) * target * track.clientWidth, behavior: behavior() });
    };

    const sync = () => {
      const i = Math.round(Math.abs(track.scrollLeft) / Math.max(1, track.clientWidth));
      if (i === index && gallery.dataset.synced) return;
      gallery.dataset.synced = '1';
      index = i;
      if (counter) counter.textContent = `${i + 1} / ${items.length}`;
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === items.length - 1;
      const active = thumbs[i];
      if (active && active.parentElement.scrollWidth > active.parentElement.clientWidth) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: behavior() });
      }
    };

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );

    thumbs.forEach((t) => t.addEventListener('click', () => go(parseInt(t.dataset.thumb, 10))));
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));

    track.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const forward = e.key === (isRTL() ? 'ArrowLeft' : 'ArrowRight');
      go(index + (forward ? 1 : -1));
    });

    sync();

    return {
      goToMedia(mediaId) {
        const i = items.findIndex((el) => el.dataset.mediaId === String(mediaId));
        if (i > -1) go(i);
      },
    };
  }

  /* ---------- Zoom viewer ---------- */
  function initZoom(root) {
    const zoom = $('[data-zoom]', root);
    if (!zoom) return;
    root.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-zoom-open]');
      if (!opener) return;
      e.preventDefault();
      $$('img[data-zoom-src]', zoom).forEach((img) => {
        if (!img.getAttribute('src')) img.src = img.dataset.zoomSrc;
      });
      GJ.drawer.open(zoom, opener);
      const target = $(`img[data-zoom-index="${opener.dataset.zoomOpen}"]`, zoom);
      if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    });
  }

  /* ---------- Variant picker + add to bag ---------- */
  function initForm(root, gallery) {
    const form = $('[data-product-form]', root);
    if (!form) return;
    const s = cfg().strings;
    const dataEl = $('[data-variants]', root);
    const variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    const radios = $$('[data-variant-radio]', form);
    const multi = $$('[data-option-multi]', form);
    const hiddenId = $('[data-variant-id]', form);
    const addBtn = $('[data-add-button]', form);
    const addLabel = $('[data-add-label]', form);
    const stock = $('[data-stock]', form);
    const group = $('[data-option-group]', form);
    const errorEl = $('[data-option-error]', form);
    const selectedLabel = $('[data-selected-label]', form);
    const priceWrap = $('[data-price-wrap]', root);
    const installments = $('[data-installments] strong', root);
    const installmentCount = parseInt(($('[data-installments]', root) || {}).dataset?.count, 10) || 4;
    const sticky = $('[data-sticky-atc]', root);
    const stickyBtn = $('[data-sticky-add]', root);
    const stickyPrice = $('[data-sticky-price]', root);
    const threshold = parseInt(root.dataset.lowThreshold, 10) || 0;
    const priceVaries = new Set(variants.map((v) => `${v.price}-${v.compare}`)).size > 1;

    const current = () => {
      if (radios.length) {
        const r = radios.find((x) => x.checked);
        return r ? variants.find((v) => v.id === Number(r.value)) || null : null;
      }
      if (multi.length) {
        const chosen = multi.map((fs) => {
          const c = $('input:checked', fs);
          return c ? c.value : null;
        });
        return variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;
      }
      return variants.find((v) => hiddenId && v.id === Number(hiddenId.value)) || variants[0] || null;
    };

    const priceHTML = (v) => {
      const sale = v.compare > v.price;
      return `<div class="price${sale ? ' price--sale' : ''}" data-price><span class="price__current">${GJ.formatMoney(v.price)}</span>${
        sale ? `<s class="price__compare">${GJ.formatMoney(v.compare)}</s>` : ''
      }</div>`;
    };

    const update = (v, fromUser) => {
      const label = !v ? s.selectSize : v.available ? s.addToCart : s.soldOut;
      if (addLabel) addLabel.textContent = label;
      if (addBtn) addBtn.disabled = !!v && !v.available;
      if (stickyBtn) {
        stickyBtn.textContent = label;
        stickyBtn.disabled = !!v && !v.available;
      }
      if (multi.length && hiddenId && v) hiddenId.value = v.id;

      if (selectedLabel && radios.length) {
        const r = radios.find((x) => x.checked);
        selectedLabel.textContent = r ? r.dataset.value : '';
      }
      if (multi.length) {
        multi.forEach((fs) => {
          const c = $('input:checked', fs);
          const l = $('[data-selected-label]', fs);
          if (l) l.textContent = c ? c.value : '';
        });
      }

      if (stock) {
        stock.classList.remove('is-low');
        if (v && v.available) {
          const low = v.qty !== null && v.qty > 0 && v.qty <= threshold;
          stock.classList.toggle('is-low', low);
          const text = low ? (s.lowStock || '').replace('[count]', v.qty) : s.inStock;
          stock.innerHTML = `<span class="pdp__stock-dot"></span><span>${text}</span>`;
        } else {
          stock.innerHTML = '';
        }
      }

      if (v && priceVaries) {
        if (priceWrap) priceWrap.innerHTML = priceHTML(v);
        if (stickyPrice) stickyPrice.innerHTML = priceHTML(v);
        if (installments) installments.innerHTML = GJ.formatMoney(Math.floor(v.price / installmentCount));
      }

      if (v && group) {
        group.classList.remove('is-error');
        if (errorEl) errorEl.hidden = true;
      }

      if (v && fromUser) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', url.toString());
        if (v.media && gallery) gallery.goToMedia(v.media);
      }
    };

    const promptChoice = () => {
      if (!group) return;
      group.classList.remove('is-error');
      void group.offsetWidth;
      group.classList.add('is-error');
      if (errorEl) errorEl.hidden = false;
      const rect = group.getBoundingClientRect();
      if (rect.top < 80 || rect.bottom > window.innerHeight) {
        group.scrollIntoView({ block: 'center', behavior: behavior() });
      }
      const firstEnabled = radios.find((r) => !r.disabled);
      if (firstEnabled) firstEnabled.focus({ preventScroll: true });
    };

    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-variant-radio], [data-multi-value]')) update(current(), true);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const v = current();
      if (!v) {
        promptChoice();
        return;
      }
      if (!v.available || !addBtn || addBtn.classList.contains('is-loading')) return;
      addBtn.classList.add('is-loading');
      addBtn.setAttribute('aria-busy', 'true');
      try {
        await GJ.cart.add([{ id: v.id, quantity: 1 }]);
        addBtn.classList.remove('is-loading');
        addBtn.classList.add('is-done');
        setTimeout(() => addBtn.classList.remove('is-done'), 1800);
        GJ.cart.open(addBtn);
      } catch (err) {
        addBtn.classList.remove('is-loading');
        GJ.toast(err.message);
      } finally {
        addBtn.removeAttribute('aria-busy');
      }
    });

    if (sticky && addBtn) {
      /* Show the bar once the main button has scrolled above the viewport */
      const setSticky = (show) => {
        if (sticky.classList.contains('is-visible') === show) return;
        sticky.classList.toggle('is-visible', show);
        if (show) sticky.removeAttribute('inert');
        else sticky.setAttribute('inert', '');
      };
      const check = () => setSticky(addBtn.getBoundingClientRect().bottom < 0);
      let queued = false;
      window.addEventListener(
        'scroll',
        () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            check();
          });
        },
        { passive: true }
      );
      if ('IntersectionObserver' in window) new IntersectionObserver(check).observe(addBtn);
      check();
    }

    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        if (!current()) {
          promptChoice();
          return;
        }
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }

    update(current(), false);
  }

  /* ---------- Share ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-link]');
    if (!btn) return;
    e.preventDefault();
    const url = btn.dataset.copyLink;
    if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      try {
        await navigator.share({ title: document.title, url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      GJ.toast(btn.dataset.copiedText);
    } catch (err) {
      window.prompt('', url);
    }
  });

  /* ---------- Recently viewed ---------- */
  const RECENT_KEY = 'gj_recent';

  function trackRecent(handle) {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
      const next = [handle, ...list.filter((h) => h !== handle)].slice(0, 12);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) {
      /* storage blocked */
    }
  }

  async function initRecent(scope = document) {
    const el = $('[data-recently-viewed]', scope);
    if (!el || el.dataset.loaded) return;
    el.dataset.loaded = '1';
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
    } catch (e) {
      list = [];
    }
    list = list.filter((h) => h !== el.dataset.current).slice(0, parseInt(el.dataset.limit, 10) || 8);
    if (!list.length) return;
    const base = (cfg().routes.root || '/').replace(/\/$/, '');
    const cards = await Promise.all(
      list.map((h) =>
        fetch(`${base}/products/${encodeURIComponent(h)}?view=card`)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      )
    );
    const html = cards
      .map((c) => c.trim())
      .filter((c) => c.indexOf('data-product-card') > -1)
      .map((c) => `<div class="slide">${c}</div>`)
      .join('');
    if (!html) return;
    const track = $('[data-slider-track]', el);
    track.innerHTML = html;
    el.hidden = false;
    if (GJ.reveal) GJ.reveal(el);
    if (GJ.sliders) GJ.sliders(el);
    if (GJ.wishlist) GJ.wishlist.sync();
  }

  /* ---------- Recommendations (lazy) ---------- */
  function initRecommendations(scope = document) {
    $$('[data-recommendations]', scope).forEach((el) => {
      if (el.dataset.loaded) return;
      const load = async () => {
        el.dataset.loaded = '1';
        try {
          const res = await fetch(el.dataset.url);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = $('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            if (GJ.reveal) GJ.reveal(el);
            if (GJ.sliders) GJ.sliders(el);
            if (GJ.wishlist) GJ.wishlist.sync();
          }
        } catch (e) {
          /* recommendations are optional */
        }
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '600px 0px' }
      );
      io.observe(el);
    });
  }

  /* ---------- Init ---------- */
  function init(scope = document) {
    $$('[data-product]', scope).forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const gallery = initGallery(root);
      initZoom(root);
      initForm(root, gallery);
      if (root.dataset.handle) trackRecent(root.dataset.handle);
    });
    initRecommendations(scope);
    initRecent(scope);
  }

  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
